import { AuthService } from '../../auth/services/auth.service';
import { StorageService } from '../../auth/services/storage.service';
import { ApiError } from '../../auth/types/auth.types';
import { API_URL } from '../utils/util';

let passwordChangeRequiredHandler: ((error: any) => void) | null = null;
let refreshTokenSuccessHandler: (() => void) | null = null;

/**
 * Establece el handler global para PASSWORD_CHANGE_REQUIRED
 */
export const setPasswordChangeRequiredHandler = (handler: (error: any) => void) => {
  passwordChangeRequiredHandler = handler;
};

/**
 * Establece el handler global para REFRESH_TOKEN_SUCCESS
 */
export const setRefreshTokenSuccessHandler = (handler: () => void) => {
  refreshTokenSuccessHandler = handler;
};

/**
 * Cliente HTTP personalizado con manejo automático de errores de autenticación
 */
export class ApiClient {
  /**
   * Realiza una petición HTTP con manejo automático de errores y refresh token
   * 
   * @param endpoint - Endpoint de la API (sin el prefijo del URL base)
   * @param options - Opciones de la petición fetch
   * @param isRetry - Indica si es un reintento después de refresh token
   * @returns Promise con la respuesta de la API
   */
  static async request<T = any>(
    endpoint: string, 
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    
    const accessToken = StorageService.getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData: ApiError = await response.json().catch(() => ({
          message: 'Request failed',
          statusCode: response.status,
        }));

        const error = new Error(errorData.message || 'Request failed') as Error & ApiError;
        error.statusCode = errorData.statusCode || response.status;
        error.code = errorData.code;

        if (response.status === 401 && !isRetry) {
          try {
            await AuthService.refreshToken();
            
            if (refreshTokenSuccessHandler) {
              refreshTokenSuccessHandler();
            }
            
            return this.request<T>(endpoint, options, true);
          } catch (refreshError) {
            console.error('Token refresh failed, logging out:', refreshError);
            AuthService.logout();
            
            if (typeof window !== 'undefined') {
              window.location.href = '/auth/login';
            }
            
            throw error;
          }
        }

        if (AuthService.isPasswordChangeRequired(error)) {
          if (passwordChangeRequiredHandler) {
            passwordChangeRequiredHandler(error);
          }
        }

        throw error;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return (await response.text()) as unknown as T;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Realiza una petición GET
   */
  static async get<T = any>(endpoint: string, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Realiza una petición POST
   */
  static async post<T = any>(
    endpoint: string, 
    data?: any, 
    options: Omit<RequestInit, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Realiza una petición PUT
   */
  static async put<T = any>(
    endpoint: string, 
    data?: any, 
    options: Omit<RequestInit, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Realiza una petición PATCH
   */
  static async patch<T = any>(
    endpoint: string, 
    data?: any, 
    options: Omit<RequestInit, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Realiza una petición DELETE
   */
  static async delete<T = any>(endpoint: string, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}
